// Import react elements
import { useContext, useEffect, useRef, useState } from "react";

// Import strich api calls
import { updateAccountSettings, getUserAccountData } from "api/APIStrichCalls";

// Import context function
import { UserContext } from "global/context/UserContext";
import { AuthContext } from "global/context/AuthContext";

// Import hooks
import usePageTitle from "global/hooks/usePageTitle";
import { useToast } from "global/hooks/useToast";

// Import components
import { Grid, GridItem } from "global/components/Grid";
import AccountDetails from "./components/AccountDetails";
import AffiliateReferral from "./components/AffiliateReferral";
//import UpdatePassword from "./components/UpdatePassword";
//import LinkPayPalMerchant from "./components/LinkPayPalMerchant";
import LinkPayPalBilling from "./components/LinkPayPalBilling";
import PayPalPayoutEmail from "./components/PayPalPayoutEmail";
import HelpButton from "global/components/HelpButton";

import StripeBillingManager from "./components/StripeBillingManager";
import SubscriptionLevel from "./components/SubscriptionLevel";

import { LoadingSpinner } from "global/components/loading_ui/LoadingSpinnerAnimation";

// Import css stylesheet
import "./AccountPage.css";



// AccountPage.js
const Account = () => {


    // Set title inside the page
    usePageTitle("Account");

    // Get the auth token
    const { token } = useContext(AuthContext);
    
    // Extract the `userData` from the authentication context - allows us to call userData anywhere inside this component without needing to manually pass it down as a prop
    const { userData, setUserData, userDataErrorMessage } = useContext(UserContext);

    // Define toast options
    const { toastSuccess, toastError } = useToast();    

    // This stores the fixed billing method keys
    const BILLING_METHODS = {
        balance: "balance",
        stripe: "stripe",
        paypal: "paypal",
    };
    
    // Store if paypal billing is linked
    const paypalBillingIsLinked = userData?.paypal_billing_link_date ? true : false;

    // Store if stripe billing is linked
    const stripeBillingIsLinked = userData?.stripe_billing_last4 ? true : false;



    // This safely turns the billing_methods setting into an ordered array
    const getBillingMethodOrder = (billingMethodsSetting) => {

        // This is the fallback order
        const defaultOrder = ["balance", "stripe", "paypal"];

        // Return the default order if no setting exists
        if (!billingMethodsSetting) return defaultOrder;

        // Split the setting into an array
        const savedOrder = billingMethodsSetting.split(",").filter(Boolean);

        // Add any missing methods so the UI never loses cards
        const completedOrder = [
            ...savedOrder,
            ...defaultOrder.filter((method) => !savedOrder.includes(method)),
        ];

        // Keep balance first if you decide to mandate it
        return completedOrder;

    };


    // Helper to get the billing methods setting from the user account data
    const getBillingMethodsSetting = () => {

        const billingMethodsSetting = userData?.settings?.find((setting) => setting.code === "billing_methods");

        return billingMethodsSetting;

    };



    // This stores the current billing method order
    const [billingMethodOrder, setBillingMethodOrder] = useState(() => {

        const billingMethodsSetting = getBillingMethodsSetting();

        // Return the saved billing method order
        return getBillingMethodOrder(billingMethodsSetting?.value);

    });



    // Update the billing order when the userData 
    useEffect(() => {

        if (!userData) return;

        const billingMethodsSetting = getBillingMethodsSetting();

        const initialValue = getBillingMethodOrder(billingMethodsSetting?.value);

       setBillingMethodOrder(initialValue);

        // Re-evaluate when the userdata loads
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData]);



    // This stores the method currently being dragged
    const [draggedMethod, setDraggedMethod] = useState(null);

    // This stores the method currently underneath the dragged pointer
    const [dragOverMethod, setDragOverMethod] = useState(null);

    // Track when the billing order was deliberately changed by the user
    const billingOrderManuallyChanged = useRef(false);



    // This checks if a billing method is linked and can be used
    const billingMethodIsLinked = (method) => {

        // Balance is always available
        if (method === BILLING_METHODS.balance) return true;

        // Return whether PayPal billing is linked
        if (method === BILLING_METHODS.paypal) return paypalBillingIsLinked;

        // Return whether Stripe billing is linked
        if (method === BILLING_METHODS.stripe) return stripeBillingIsLinked;

        // Unknown methods are not usable
        return false;

    };



    // This stores the billing methods that are active and included in priority order
    const activeBillingMethodOrder = billingMethodOrder.filter((method) => billingMethodIsLinked(method));

    // This stores the billing methods that are not linked yet
    const inactiveBillingMethodOrder = billingMethodOrder.filter((method) => !billingMethodIsLinked(method));



    // This starts dragging a billing method
    const handleBillingDragStart = (event, method) => {

        // Stop if this billing method cannot be dragged
        if (!billingMethodIsLinked(method)) {
            return;
        }

        // Store the billing method currently being dragged
        setDraggedMethod(method);

        // Store the dragged method as the initial card underneath the pointer
        setDragOverMethod(method);

        // Capture the pointer so movement continues to be detected outside this card
        event.currentTarget.setPointerCapture(event.pointerId);

    };



    // This checks which billing method is currently underneath the pointer
    const handleBillingDragMove = (event) => {

        // Stop if no billing method is currently being dragged
        if (!draggedMethod) {
            return;
        }

        // Find the element currently underneath the mouse, finger or stylus
        const elementUnderPointer = document.elementFromPoint(
            event.clientX,
            event.clientY
        );

        // Find the billing method card containing that element
        const billingMethodCard = elementUnderPointer?.closest(
            "[data-billing-method]"
        );

        // Stop if the pointer is not currently over a billing method card
        if (!billingMethodCard) {
            return;
        }

        // Get the billing method stored against the card
        const targetMethod = billingMethodCard.dataset.billingMethod;

        // Stop if this billing method is not active and linked
        if (!billingMethodIsLinked(targetMethod)) {
            return;
        }

        // Store the card currently underneath the pointer
        setDragOverMethod(targetMethod);

    };



    // This finishes dragging and moves the billing method
    const handleBillingDragEnd = () => {

        // Stop if nothing is currently being dragged
        if (!draggedMethod) {
            return;
        }

        // Store the target before state is cleared
        const targetMethod = dragOverMethod;

        // Move the billing method if it was dropped over another method
        if (targetMethod && targetMethod !== draggedMethod) {

            // Move the billing method
            moveBillingMethod(targetMethod);

        } else {

            // Clear the dragged billing method
            setDraggedMethod(null);

        }

        // Clear the current drag-over billing method
        setDragOverMethod(null);

    };



    // This moves one active billing method before another active billing method
    const moveBillingMethod = (targetMethod) => {

        // Stop if there is no dragged method
        if (!draggedMethod) {

            // Clear the dragged method
            setDraggedMethod(null);

            return;

        }

        // Stop if the dragged method is not linked
        if (!billingMethodIsLinked(draggedMethod)) {

            // Clear the dragged method
            setDraggedMethod(null);

            return;

        }

        // Stop if the target method is not linked
        if (!billingMethodIsLinked(targetMethod)) {

            // Clear the dragged method
            setDraggedMethod(null);

            return;

        }

        // Stop if the user dragged onto itself
        if (draggedMethod === targetMethod) {

            // Clear the dragged method
            setDraggedMethod(null);

            return;

        }

        // Find the original positions of the dragged and target methods
        const draggedIndex = activeBillingMethodOrder.indexOf(draggedMethod);
        const originalTargetIndex = activeBillingMethodOrder.indexOf(targetMethod);

        // Stop if either method cannot be found
        if (draggedIndex === -1 || originalTargetIndex === -1) {

            // Clear the dragged method
            setDraggedMethod(null);

            return;

        }

        // Create a list without the dragged method
        const filteredActiveOrder = activeBillingMethodOrder.filter((method) => method !== draggedMethod);

        // Find where the target method now sits
        let targetIndex = filteredActiveOrder.indexOf(targetMethod);

        // Stop if the target method cannot be found
        if (targetIndex === -1) {

            // Clear the dragged method
            setDraggedMethod(null);

            return;

        }

        // Insert after the target when moving a method downwards
        if (draggedIndex < originalTargetIndex) {

            targetIndex += 1;

        }

        // Insert the dragged method in its new position
        filteredActiveOrder.splice(targetIndex, 0, draggedMethod);

        // This order change was deliberately made by the user
        billingOrderManuallyChanged.current = true;

        // Keep inactive methods at the bottom
        setBillingMethodOrder([...filteredActiveOrder, ...inactiveBillingMethodOrder]);

        // Clear the dragged method
        setDraggedMethod(null);

    };

  

    // This saves the billing method preference setting
    const saveBillingMethodOrder = async () => {
        
        // Convert the order back into the API setting format
        const billingMethodsValue = activeBillingMethodOrder.join(",");

        // Get the billing method setting safely
        const billingSetting = getBillingMethodsSetting();

        // Do not create a billing preference merely because the Account page was opened
        if (!billingSetting && !billingOrderManuallyChanged.current) return;

        // If the current value matches the existing then return early because nothing changed
        if (billingMethodsValue === billingSetting?.value) {

            billingOrderManuallyChanged.current = false;

            return;

        }

        // Build the payload 
        let payload;

        // If there's no current setting then we need to add the whole setting
        if (!billingSetting) {

            payload = {          
                type: "Billing",
                code: "billing_methods",
                name: "Billing Methods",
                description: "The billing methods to use and the order to use them",
                value: billingMethodsValue,          
            };

        } else {

            // There's an existing setting so use it but add the value
            payload = {     
                ...billingSetting,
                value: billingMethodsValue,          
            };

        }

        // Send billingMethodsValue to your new settings API call
        const response = await updateAccountSettings(token, payload);

        if (response.success) {

            // Refetch the updated user data
            getUserAccountData(token, setUserData)

            // Only show a success message when the user deliberately reordered the methods
            if (billingOrderManuallyChanged.current) {

                toastSuccess({ title: "Success", message: "Billing order updated." });

            }

            // Clear the manual-change flag
            billingOrderManuallyChanged.current = false;

        } else {

            // Clear the manual-change flag
            billingOrderManuallyChanged.current = false;

            // Toast error message
            toastError({ title: "Error", message: "Could not update billing order: " + response?.error_message, autoCloseMs: 6000 });

        }

    };



    // Update the billing methods setting when the order changes
    useEffect(() => {

        if (!userData) return;

        saveBillingMethodOrder();

        // Re-evaluate when the order of the billing methods changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [billingMethodOrder]);







    


    // This renders one billing method card
    const renderBillingMethodCard = (method, isActive) => {

        // Store whether this card can be dragged
        const canDrag = isActive && billingMethodIsLinked(method);

        // Return balance card
        if (method === "balance") {

            return (

                <div 
                    // Give React a stable key for this billing method
                    key="balance"

                    // Store the billing method against the DOM element
                    data-billing-method="balance"

                    // Show information about whether the card can be reordered
                    title={canDrag ? "Drag to reorder" : "Connect this method to include it"}

                    // Start dragging using mouse, touch or stylus
                    onPointerDown={(event) => canDrag && handleBillingDragStart(event, "balance")}

                    // Apply the normal, inactive, dragging and drag-over styles
                    className={`account-billing-preference__card ${!isActive ? "account-billing-preference__card--inactive" : ""} ${draggedMethod === method ? "account-billing-preference__card--dragging" : ""} ${dragOverMethod === method && draggedMethod !== method ? "account-billing-preference__card--drag-over" : ""}`}
                >

                    <div className="billing-method-container">

                        <div className={`horizontal-align-content-center account-billing-preference__header ${isActive ? "account-billing-preference__header--active" : "account-billing-preference__header--inactive"}`}><h3>Balance</h3></div>

                        <br/>

                        <p>Use your account balance for automatic billing.</p>

                    </div>

                </div>
            );

        }

        // Return PayPal card
        if (method === "paypal") {

            return (

                <div
                    // Give React a stable key for this billing method
                    key="paypal"

                    // Store the billing method against the DOM element
                    data-billing-method="paypal"

                    // Show information about whether the card can be reordered
                    title={canDrag ? "Drag to reorder" : "Connect PayPal to include it"}

                    // Start dragging using mouse, touch or stylus
                    onPointerDown={(event) => canDrag && handleBillingDragStart(event, "paypal")}

                    // Apply the normal, inactive, dragging and drag-over styles
                    className={`account-billing-preference__card ${!isActive ? "account-billing-preference__card--inactive" : ""} ${draggedMethod === method ? "account-billing-preference__card--dragging" : ""} ${dragOverMethod === method && draggedMethod !== method ? "account-billing-preference__card--drag-over" : ""}`}
                >

                    <LinkPayPalBilling
                        userData={userData}
                        setUserData={setUserData}
                    />

                </div>

            );

        }

        // Return Stripe card
        if (method === "stripe") {

            return (

                <div
    // Give React a stable key for this billing method
    key="stripe"

    // Store the billing method against the DOM element
    data-billing-method="stripe"

    // Show information about whether the card can be reordered
    title={canDrag ? "Drag to reorder" : "Connect Stripe to include it"}

    // Start dragging using mouse, touch or stylus
    onPointerDown={(event) => canDrag && handleBillingDragStart(event, "stripe")}

    // Apply the normal, inactive, dragging and drag-over styles
    className={`account-billing-preference__card ${!isActive ? "account-billing-preference__card--inactive" : ""} ${draggedMethod === method ? "account-billing-preference__card--dragging" : ""} ${dragOverMethod === method && draggedMethod !== method ? "account-billing-preference__card--drag-over" : ""}`}
>

                    <StripeBillingManager userData={userData} setUserData={setUserData} />

                </div>

            );

        }

        // Return nothing for unknown methods
        return null;

    };


    // Check that userData exists and is not an empty object
    const hasUserData =
        userData &&
        typeof userData === "object" &&
        Object.keys(userData).length > 0;



    // If userData is missing or empty then show a loading state
    if (!hasUserData) {

        return (

            <Grid>
            
                <GridItem>

                    
                    {userDataErrorMessage ?

                        // If there is an error message from the user account details display it
                        <div className="dashboard-status-panel dashboard-status-panel--error">

                            <h3>Account unavailable</h3>
                            <p>{userDataErrorMessage}</p>

                        </div>
                
                    :
                
                        <div className="account-loading">

                            <LoadingSpinner />
                            <p>Loading account details...</p>

                        </div>
                
                    }
                   
                </GridItem>

            </Grid>
            
        );

    }



    return (

        <>

           <Grid>

                <AccountDetails userData={userData} />

                <SubscriptionLevel userData={userData} setUserData={setUserData} />

            </Grid>

            <Grid>

                <PayPalPayoutEmail userData={userData} />

                <AffiliateReferral userData={userData} />

                {/*<LinkPayPalMerchant userData={userData} />*/}

            </Grid>

            <Grid>

                <GridItem>
                    
                    <h3>Billing <HelpButton section="account" code="billing-methods" label="Billing Methods" /></h3>

                    <p>Drag and drop the billing methods in order of preference.</p>

                    <br/>

                    <div className="account-billing-preference">

                        <div
                            // Track movement while a billing method is being dragged
                            onPointerMove={handleBillingDragMove}

                            // Finish the drag when the mouse, finger or stylus is released
                            onPointerUp={handleBillingDragEnd}

                            // Clean up if the browser cancels the pointer interaction
                            onPointerCancel={handleBillingDragEnd}

                            // Apply the existing active billing method list styling
                            className="account-billing-preference__active-list"
                        >

                            {activeBillingMethodOrder.map((method) => {

                                // Render each active billing method
                                return renderBillingMethodCard(method, true);

                            })}

                        </div>

                        {inactiveBillingMethodOrder.length > 0 && (

                            <div className="account-billing-preference__inactive-section">

                                {/* 

                                <h4>Not included in billing priority</h4>

                                <p>Connect these payment methods before they can be used for automatic billing.</p>
                                
                                */}
                                
                                <div className="account-billing-preference__inactive-list">

                                    {inactiveBillingMethodOrder.map((method) => {

                                        return renderBillingMethodCard(method, false);

                                    })}

                                </div>

                            </div>

                        )}

                    </div>

                </GridItem>

            </Grid>

        </>
        
    );

};

export default Account;